import React, { useState } from 'react';
import { vSIGetCustomerLoyaltyCardAsyncAsync } from '../../actions/vsi-customer-loyalty-extension/DataActionExtension.g';
import { ModalHeader, Modal, Waiting } from '@msdyn365-commerce-modules/utilities';
import { IDobbiesFreeTrialCancellationData } from './dobbies-free-trial-cancellation.data';
import { IDobbiesFreeTrialCancellationProps } from './dobbies-free-trial-cancellation.props.autogenerated';

interface IStatusBoxProps {
    title: string;
    text: React.ReactNode;
    button?: string;
    onClick?: () => void;
    className?: string;
    hideButton?: boolean | string;
    yellow?: boolean;
}
export default function DobbiesFreeTrialCancellation(props: IDobbiesFreeTrialCancellationProps<IDobbiesFreeTrialCancellationData>) {
    const [cardNumber, setCardNumber] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [validToDate, setValidToDate] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [result, setResult] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [tierId, setTierId] = useState<string>();

    const isCardValid = cardNumber.length === 13 && /^\d{13}$/.test(cardNumber);
    const isEmailValid = /\S+@\S+\.\S+/.test(email);
    const canSubmit = isCardValid && isEmailValid && !loading;

    const handleCancelFreeTrial = async () => {
        try {
            setShowModal(false);
            setLoading(true);

            const response = await vSIGetCustomerLoyaltyCardAsyncAsync(
                { callerContext: props.context.actionContext },
                cardNumber,
                'DGC',
                email
            );

            switch (true) {
                case !response.CardStatus || !response.EmailStatus:
                    setResult('not-found');
                    return;

                case !response.TierStatus && !response.Status:
                    setTierId(response.TierId);
                    setResult('active-club');
                    return;

                case !response.ValidDateStatus && !response.Status:
                    setResult('expires-soon');
                    return;

                case response.Status && response.TierStatus && response.ValidDateStatus:
                    const validToRaw = response.TierValidTo ? new Date(response.TierValidTo) : null;
                    if (validToRaw && !isNaN(validToRaw.getTime())) {
                        const formatted = `${validToRaw
                            .getDate()
                            .toString()
                            .padStart(2, '0')}/${(validToRaw.getMonth() + 1).toString().padStart(2, '0')}/${validToRaw
                            .getFullYear()
                            .toString()
                            .slice(-2)}`;

                        setValidToDate(formatted);
                    }

                    setResult('cancelled');
                    return;

                default:
                    setResult('not-found');
                    return;
            }
        } catch {
            setResult('not-found');
        } finally {
            setLoading(false);
        }
    };

    const renderStatus = () => {
        if (!result) {
            return null;
        }

        switch (result) {
            case 'not-found':
                return (
                    <StatusBox
                        className='not-found'
                        title='We couldn’t find an account with the details provided.'
                        text={
                            <>
                                Please check that your information matches the correct details for your membership (email address and
                                temporary card number).
                                <br></br>
                                <br></br>
                                If you believe this is an error, please contact our{' '}
                                <a href={props.config.customerServicesUrl}>customer services team</a> and include any relevant information
                                to help us locate your account.
                            </>
                        }
                        button='Go back to the form'
                        onClick={() => setResult(null)}
                    />
                );

            case 'active-club':
                return (
                    <StatusBox
                        className='active-club'
                        title={`You have an active ${tierId} account.`}
                        text={
                            <>
                                Our records show your account is a valid {tierId} membership and not a free trial account. Please check that
                                your information matches the correct details for your free trial membership (email address and temporary
                                card number).
                                <br></br>
                                <br></br>
                                If you believe this is an error, please contact our{' '}
                                <a href={props.config.customerServicesUrl}>customer services team</a> and include any relevant information
                                to help us locate your account.
                            </>
                        }
                        button='Go back to the form'
                        onClick={() => setResult(null)}
                    />
                );

            case 'expires-soon':
                return (
                    <StatusBox
                        className='expires-soon'
                        title='Your free trial expires soon'
                        text={
                            <>
                                Our records show your free trial membership expires within the next 48 hours which means we can’t process
                                your cancellation online.
                                <br></br> <br></br>Please contact our <a href={props.config.customerServicesUrl}>customer services team</a>{' '}
                                before the expiry date to have them process this manually.
                            </>
                        }
                        hideButton='true'
                        onClick={() => setResult(null)}
                    />
                );

            case 'cancelled':
                return (
                    <StatusBox
                        className='cancelled'
                        title='Your free trial is cancelled'
                        text={
                            <>
                                Your Club Plus benefits will remain active until <b>{validToDate}</b>. After this date, you’ll move to our
                                free Club membership but you can upgrade to Club Plus at any time in-store or online.
                            </>
                        }
                        button='Back to shopping'
                        onClick={() => {
                            window.location.href = props.config?.backToShoppingUrl || '/';
                        }}
                        yellow
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div className='ftc-form-container'>
            {loading && <Waiting />}
            {!loading && result && renderStatus()}
            {!loading && !result && (
                <div className='ftc-form'>
                    <p>
                        To cancel your free trial membership, please enter the email address and temporary card number of your free trial
                        account in the fields below.
                    </p>

                    <div className='ftc-form-inputs'>
                        <div>
                            <label>Temporary membership card number*</label>
                            <input
                                type='text'
                                value={cardNumber}
                                onChange={e => setCardNumber(e.target.value)}
                                placeholder='Enter the 13 digits of your card'
                            />
                            {!isCardValid && cardNumber.length > 0 && <p>Card number must be exactly 13 digits.</p>}
                        </div>

                        <div>
                            <label>Email address*</label>
                            <input type='email' value={email} onChange={e => setEmail(e.target.value)} />
                            {!isEmailValid && email.length > 0 && <p>Please enter a valid email.</p>}
                        </div>
                    </div>

                    <p>Are you sure you want to give up 24 free hot drinks?</p>

                    <button onClick={() => setShowModal(true)} disabled={!canSubmit} className={!canSubmit ? 'disabled' : ''}>
                        Cancel my free trial
                    </button>
                    <p className='ftAccountDetailsText'>
                        If you are unsure of your Free Trial account details, please contact our customer services team{' '}
                        <a href={props.config.customerServicesUrl}>click here</a>
                    </p>
                </div>
            )}

            <Modal isOpen={showModal} className='ftc-modal'>
                <ModalHeader className='msc-modal__header ftc-modal__dialog__header' toggle={() => setShowModal(false)} />
                <div className='ftc-modal-body'>
                    <div className='ftc-modal-image'></div>
                    <p>On average, our members save £63 a year with their Club Plus benefits.</p>

                    <p className='ftc-modal-question'>Are you sure you want to continue with the cancellation of your membership?</p>

                    <div className='ftc-modal-footer'>
                        <button className='yellow-btn' onClick={() => setShowModal(false)}>
                            Continue free trial
                        </button>

                        <button onClick={handleCancelFreeTrial}>Cancel my free trial</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

const StatusBox = ({ title, text, button, onClick, className, hideButton, yellow }: IStatusBoxProps) => (
    <div className={`ftc-form ${className ? className : ''}`}>
        <h2>{title}</h2>
        <p>{text}</p>
        {!hideButton && (
            <button className={`ftc-btn ${yellow ? 'ftc-btn-yellow' : ''}`} onClick={onClick}>
                {button}
            </button>
        )}
    </div>
);
